import { codeUnitString } from "./code-unit-string.js";
import { FIXTURE_ID_PATTERN, SLUG_PATTERN } from "./app-admin-inputs.js";
import { MACHINE_TYPE_RESOLUTION_NOTES, computeClassSchema, sizeSchema } from "./compute-class.js";
import { reconcileRefusalCodeSchema } from "./reconcile-refusal.js";
import { refineExactlyOneForm } from "./exactly-one-form.js";
import { z } from "zod";
//#region src/compute-resolution.ts
/**
* D5/D5a/D5b — was the machine type this row RESOLVED actually OBTAINED?
*
* ⛔ A ROUTED SIGNAL, NOT A NEW OBSERVER, AND POST-DEPLOY ONLY (D4). Every obtainability signal in
* this platform derives from an EXISTING pod. Before a release there is no pod for the new version,
* so a pre-release "can this DP place class X?" check can only verify RESOLVABILITY — which is
* precisely the green that already returned in the middle of the 2026-08-09 outage. If you find
* yourself designing a pre-release gate, stop.
*
* The producer is `isPendingUnschedulable`
* (`cloud/dp/zion-operator/src/reconciler/scaling-event-reconciler.ts:144-147`), *"THE single
* definition of the node-scale observation"*. ⛔ It is EDGE-TRIGGERED — *"a steady-state poll emits
* NOTHING"* — so the scaling-event stream cannot answer a STATE question. That is why the state rides
* here, on the resolution row, and not on the event.
*
* ⛔⛔ ABSENCE MEANS **NOT MEASURED**. IT NEVER MEANS "OBTAINABLE".
* A discriminated union from day one, not `obtainable?: boolean` — because a nullable boolean is one
* `?? true` away from an invented green on the one screen an operator uses to decide whether an app is
* broken, and because `false` cannot carry WHY. Same decision `app-promotion-readiness.ts` records for
* `PromotionErrorBudget` (*"A nullable number is one `?? 0` away from '0 % errors' on a promotion
* screen, and `null` cannot carry WHY"*), and the same rendering rule
* `internal-bootstrap.schemas.ts`' `unschedulable` field states: render absence as **"not reported"**,
* never as "schedulable".
*
* ⛔ THREE ARMS, AND THE SET IS CLOSED (D5b). "Not attempted" is ALREADY distinguishable — it is
* `resolvedNodeType: null` — so this union deliberately does NOT model it. Do not add a fourth arm, do
* not add a verdict field, and do not add a member to {@link reconcileRefusalCodeSchema} for it (D8).
*
* - `unobserved` — we looked and there is no pod to observe. ⭐ THE HONEST COMMON CASE, and the arm
*   that prevents the invented green: a version just pinned has no pod yet. `reason` is an
*   operator-facing sentence rendered VERBATIM, bounded at 512 **UTF-16 CODE UNITS** — matching the
*   bound the scaling-event contract already puts on cluster-sourced text.
*   ⛔ **CODE UNITS, NOT BYTES, AND `.max()` CANNOT BOUND BYTES.** `codeUnitString().max(512)` counts
*   `String.prototype.length` (zod 4.6's stock `z.string().max` counts code points), so a
*   512-character CJK reason is **1,536 bytes** and parses clean.
*   MEASURED 2026-08-14 against this very schema: `512 × '中'` ⇒ `success: true`, 1,536 B;
*   `513 × '中'` ⇒ `success: false` — the refusal at 513 is what proves the unit is code units and
*   not bytes. ⇒ **If a downstream budget is expressed in BYTES, `.max()` does not enforce it and a
*   second constant will not either.** The producer must make it byte-bounded BY CONSTRUCTION
*   (ASCII-only text, sanitise before slicing), which is exactly what the operator-side producer
*   does; do not remove that discipline on the grounds that "the schema already bounds it."
* - `schedulable` — a pod for this unit is running on `nodeName`. The ONLY arm that is evidence of
*   obtainability, and it is evidence because a node exists and is named.
* - `unschedulable` — a pod is `Pending`, `PodScheduled=False`, reason `Unschedulable`, and has been
*   for `sinceSeconds`. The duration carries the meaning: a two-second `Unschedulable` is a
*   normal scale-up; a twenty-minute one is the outage. A bare boolean would page on the first.
*   The arm carries the duration in one of two forms, and exactly one
*   (2026-10-10-status-report-since-timestamps D-2, D-3):
*   - `sinceSeconds`: the age in whole seconds at report time. An operator sends this form when the
*     last fresh desired-state envelope did not carry `statusReportTimestamps: true`, and every
*     operator that predates D-2 sends it. A stored row from such an operator keeps it.
*   - `since`: the ISO 8601 instant of the pod's `PodScheduled.lastTransitionTime`, the earliest of
*     the unit's pending pods, as `new Date(ms).toISOString()` writes it. The house instant shape of
*     `reportedAt`: `z.string().min(1)`, here also `.max(64)`. The value stays the same while the pod
*     stays pending, so the `computeResolutions` section etag stays the same between cycles. The
*     reader derives the age as its own clock minus `Date.parse(since)` (D-5).
*   A row with both keys, or with neither, is a `ZodError` with a custom issue at the arm's root
*   (D-3). Both keys are optional in the shape, and the refine admits exactly one. This refine
*   is a deliberate exception to the "no CP-side refine on a DP-asserted row" rule of `A-186`: a row
*   with both forms keeps the section hash volatile and hides a producer bug, and D-3 rules that
*   the contract refuses it. The cost is the one that rule names: one producer slip fails the whole
*   report.
*
* ⛔ WHAT THIS DOES **NOT** CARRY, AND WHY (D7, REFUSED). No GCE / cluster-autoscaler error text. It
* is not reachable at the DP — the operator reads pods and nodes and cannot read the GKE autoscaler —
* and there is a standing ruling against forwarding condition messages app-ward
* (`contracts-cp-dp/src/internal-bootstrap.schemas.ts:340-344`: *"condition MESSAGES … are free-text
* from the cluster autoscaler and are exactly the untrusted strings the scaling-event contract bounds
* at 512 bytes"*). ⚠️ That quotation is VERBATIM of the wording as it stood, and its *"512 bytes"*
* carried the same slip corrected above. Left unaltered because altering a quotation to fix its
* source is how a citation stops being a citation. ✅ **The source is now CORRECTED** — as of
* 2026-08-14 `internal-bootstrap.schemas.ts` says 512 UTF-16 code units and cites
* `scaling-event.ts:32,34` for the definition — so a reader who chases this citation will find the
* fixed wording, not the quoted one. The actual bound is `SCALING_TEXT_MAX = 512` applied as
* `codeUnitString().max(SCALING_TEXT_MAX)` in **this** package (`scalingText` in `scaling-event.ts`),
* which is why the grep for it in `contracts-cp-dp` comes back empty.
* Platform operators already get the string at CP, in **provisioning-svc**
* (`crossplane-resources.mapper.ts:26`, `:64`) — not site-admin-api. Apps get this bounded union.
*
* ⚠️ `reason` accepts the EMPTY STRING, and that is a producer obligation rather than a schema one.
* `.max(512)` with no `.min(1)`, exactly as ruled. A `.min(1)` was considered and NOT added: this
* schema is parsed CP-side against a DP-asserted array inside a `.strict()` WHOLE-REPORT parse, so a
* stricter bound turns one producer slip into a fleet-wide report failure — the trade `A-186`'s
* docblock already refuses for the `promotedWithinTier` ⇔ `notes` invariant. `A-303` pins the
* accepted-empty behaviour so nobody discovers it in production.
*/
const computeObtainabilitySchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("unobserved"),
		reason: codeUnitString().max(512)
	}).strict(),
	z.object({
		kind: z.literal("schedulable"),
		nodeName: z.string().min(1).max(256)
	}).strict(),
	z.object({
		kind: z.literal("unschedulable"),
		sinceSeconds: z.number().int().nonnegative().optional(),
		since: z.string().min(1).max(64).optional()
	}).strict().superRefine((arm, ctx) => refineExactlyOneForm(arm, ctx, "an unschedulable obtainability", "sinceSeconds", "since"))
]);
const computeResolutionSchema = z.object({
	/** D32 — the app SLUG (same keyspace as `envelope.apps` / `dp_fixture_status.app_id`). ⛔ THE
	*  CROSS-APP-LEAK GUARD: WS-20's diagnostics section filters `r.appId === slug` SERVER-SIDE, and
	*  `fixtureId` alone is NOT unique across apps (`dp_fixture_status`'s PK proves it). Without this
	*  field an app-scoped route hands app A's owner app B's fixtures, vectors and SKUs — the section
	*  built to satisfy security-audit §5 failing §5. Populate from the app being reconciled, NEVER
	*  from the fixture name.
	*
	*  ⛔ DP-ASSERTED — the INGEST fence (WS-20) is the authoritative check, not this. But the shape
	*  guard is DEFENCE IN DEPTH and costs one regex (D59/M-2): measured before it existed, this
	*  field ACCEPTED `../other-app`, `app/../x`, `%2e%2e`, `a b`, a U+202E-prefixed name and 63×`A`.
	*  If WS-20's fence lands as an equality compare alone — safe against traversal, but NOT against
	*  content — those bytes reach MCP `get_app_problems` output, the diagnostics `compute` section
	*  and CP logs. `SLUG_PATTERN` is the same keyspace `envelope.apps` uses and was one word away in
	*  an import this file already had. */
	appId: z.string().min(1).max(63).regex(SLUG_PATTERN),
	fixtureId: z.string().regex(FIXTURE_ID_PATTERN),
	/** D38(1) — rows are per-UNIT, not per-fixture: a fixture has many services and many workers,
	*  each with its own class, vector and resolution, so `fixtureId` is not a key. The two
	*  namespaces are INDEPENDENT — a service and a worker may share a name — which is why
	*  `unitKind` is required rather than inferable. */
	unit: z.string().min(1).max(253),
	unitKind: z.enum(["service", "worker"]),
	class: computeClassSchema,
	classSource: z.enum([
		"manifest",
		"override",
		"default"
	]),
	/** D33 — `null` when the multiplier came from an S-1 SUM: 1.5 is not a member of `SIZES`, and
	*  pretending otherwise is exactly the stringly-typed semantics D4 forbids. */
	size: sizeSchema.nullable(),
	sizeSource: z.enum([
		"tenant",
		"override",
		"fixed"
	]),
	/** D33 — ALWAYS TRUE, rendered "×N". Can exceed 8 on a shared DP (an S-1 sum has no ceiling in
	*  `SIZES`). This is the field a consumer computes and sorts with; `size` is provenance only. */
	multiplier: z.number().positive(),
	/** The BASE (declared, pre-multiplier) vector. */
	vector: z.object({
		cpuMilli: z.number().int().nonnegative(),
		memoryMiB: z.number().int().nonnegative()
	}).strict(),
	/** base × multiplier — what the resolver was asked for and what F-4 checked. */
	effective: z.object({
		cpuMilli: z.number().int().nonnegative(),
		memoryMiB: z.number().int().nonnegative()
	}).strict(),
	resolvedNodeType: z.string().max(63).nullable(),
	promotedWithinTier: z.boolean(),
	/** D38(2) — the resolver's own notes, carried WHOLE. A lone `promotedWithinTier` boolean discards
	*  `generation-fallback` / `derived-class-m` / `accelerator-pinned`, which are precisely the D12
	*  sentences ("we chose g2 because g4 is not here" vs "because you pinned nvidia-l4").
	*  ⛔ INVARIANT, pinned by test `A-186`: `promotedWithinTier === notes.includes('promoted')`.
	*  ⚠️ `.default([])` means a stored row predating the field parses to `[]`, so an EMPTY `notes`
	*  is NOT evidence the operator reported none. Do not build a UI claim on emptiness. */
	notes: z.array(z.enum(MACHINE_TYPE_RESOLUTION_NOTES)).default([]),
	refusal: reconcileRefusalCodeSchema.nullable(),
	/** D33 — populated whenever this resolution applied a Layer-B clamp or narrowed a Layer-C
	*  override. CP only ECHOES it; CP never clamps.
	*  ⛔ `.optional()`, i.e. ABSENT-or-object — it is NOT `.nullable()`. Every consumer tests
	*  `!== undefined`, and every producer OMITS the key rather than writing `null`; writing `null`
	*  fails the `.strict()` parse on the next read. */
	clamped: z.object({
		field: z.string().max(63),
		requested: z.string().max(128),
		applied: z.string().max(128),
		reason: z.string().max(512)
	}).strict().optional(),
	/** D5/D5a/D5b — see {@link computeObtainabilitySchema} for the whole rationale.
	*  ⛔ `.optional()`, and ABSENT IS "NOT MEASURED" — never "obtainable". A pre-change operator
	*  omits it, which is the honest answer, and a consumer renders that as "not reported".
	*  ⛔ ORDERING — CP FIRST (D6), same as `StatusReport.computeResolutions` itself: this field
	*  reaches CP inside a `.strict()` whole-report parse, so the CP that ACCEPTS must ship before the
	*  DP that EMITS.
	*
	*  ⛔⛔ **DO NOT TRUST THIS FIELD MID-ROLLOUT — IT CAN INVERT AT THE MOMENT OF MAXIMUM CONSEQUENCE
	*  (#1603).** The row that carries it identifies the unit (`appId`, `fixtureId`, `unit`,
	*  `unitKind`) but **NOT the version** — there is no `versionSlug` anywhere on it. So while two
	*  versions of one unit are simultaneously active, both emit rows agreeing on every identity
	*  field, byte-identical whenever the compute declaration is unchanged (the common case, since a
	*  rollout usually does not change it).
	*
	*  ⇒ A consumer keyed on unit identity shows **one of the two arbitrarily**, so a `schedulable`
	*  row can HIDE an `unschedulable` one for the other version — and the reader most likely to be
	*  looking at this field mid-rollout is someone deciding **continue or roll back**. That is the
	*  one moment it can lie, which is why this warning lives here rather than only in the issue.
	*
	*  ⇒ **While more than one version is live, prefer the POD as the oracle** (is a pod actually
	*  `Running` on a node of that machine type) and read this field only once the rollout has settled
	*  to a single version. A pod observation carries the version in its own labels; this row does not.
	*
	*  ⚠️ Stated here because the precedent is on file: the `'M'`-derivation docblock was correct for
	*  the undeclared case and **silent** about the declared one, and that silence propagated into two
	*  downstream agent briefs before anyone measured it. **A field that is trustworthy except at the
	*  moment of maximum consequence needs the caveat where the READER is, not in a linked issue.** */
	obtainability: computeObtainabilitySchema.optional()
}).strict();
/**
* Count cap on `StatusReport.computeResolutions`, mirroring `POD_LIFECYCLE_MAX`.
*
* ⛔ A COUNT BOUND IS NOT A PROOF THAT THE BODY FITS. release-engine's ingest body limit is the
* measured ~100 kB express default, a 413 kills the WHOLE report every cycle, and zod cannot express
* "the encoded array is under N kB". The PRODUCER owns the byte budget and the carry, and sets
* `StatusReport.computeResolutionsTruncated` when either cap drops rows (D37).
*/
const COMPUTE_RESOLUTION_MAX = 200;
//#endregion
export { COMPUTE_RESOLUTION_MAX, computeObtainabilitySchema, computeResolutionSchema };

//# sourceMappingURL=compute-resolution.js.map